/**
 * Stripe Client-Side Utilities
 * 
 * Handles client-side Stripe integration including:
 * - Loading Stripe.js
 * - Redirecting to Checkout
 * - Opening Customer Portal
 * - Purchasing credit packs
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

// Stripe publishable key from environment
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Stripe.js instance (loaded lazily)
let stripePromise = null;

/**
 * Load Stripe.js lazily
 * @returns {Promise<Stripe>} Stripe instance
 */
export async function getStripe() {
  if (!stripePromise) {
    if (!STRIPE_PUBLISHABLE_KEY) {
      console.error('Stripe publishable key not configured');
      throw new Error('Stripe is not configured. Please contact support.');
    }
    
    // Dynamically import Stripe.js
    const { loadStripe } = await import('@stripe/stripe-js');
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
}

/**
 * Check if Stripe is configured
 * @returns {boolean}
 */
export function isStripeConfigured() {
  return !!STRIPE_PUBLISHABLE_KEY;
}

/**
 * Redirect to Stripe Checkout for subscription
 * 
 * @param {string} planId - Plan to subscribe to (starter, pro, pro_plus)
 * @param {string} interval - Billing interval ('month' or 'year')
 * @param {string} teamId - Team ID for the subscription
 * @param {string} teamLeadEmail - Email of the team lead
 * @returns {Promise<void>}
 */
export async function redirectToCheckout(planId, interval, teamId, teamLeadEmail) {
  try {
    const functions = getFunctions();
    const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
    
    const { data } = await createCheckoutSession({
      planId,
      interval,
      teamId,
      email: teamLeadEmail,
      successUrl: `${window.location.origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${window.location.origin}/subscription/canceled`,
    });
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    // Redirect to Stripe Checkout
    const stripe = await getStripe();
    const { error } = await stripe.redirectToCheckout({
      sessionId: data.sessionId,
    });
    
    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('❌ Checkout redirect failed:', error);
    throw new Error(
      error.message || 'Failed to start checkout. Please try again.'
    );
  }
}

/**
 * Open Stripe Customer Portal for subscription management
 * 
 * @param {string} teamId - Team ID
 * @returns {Promise<void>}
 */
export async function openBillingPortal(teamId) {
  try {
    const functions = getFunctions();
    const createPortalSession = httpsCallable(functions, 'createCustomerPortalSession');
    
    const { data } = await createPortalSession({
      teamId,
      returnUrl: `${window.location.origin}/team-manager?tab=subscription`,
    });
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    // Redirect to Customer Portal
    window.location.href = data.url;
  } catch (error) {
    console.error('❌ Portal redirect failed:', error);
    throw new Error(
      error.message || 'Failed to open billing portal. Please try again.'
    );
  }
}

/**
 * Purchase evaluation credit pack (one-time payment)
 * 
 * @param {string} packId - Credit pack ID (eval_pack_10, eval_pack_20, etc.)
 * @param {string} teamId - Team ID
 * @param {string} email - Billing email
 * @returns {Promise<void>}
 */
export async function purchaseEvalCredits(packId, teamId, email) {
  try {
    const functions = getFunctions();
    const createCheckoutSession = httpsCallable(functions, 'createCreditPackCheckout');
    
    const { data } = await createCheckoutSession({
      packId,
      teamId,
      email,
      successUrl: `${window.location.origin}/subscription/credits-added?pack=${packId}`,
      cancelUrl: `${window.location.origin}/team-manager?tab=subscription`,
    });
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    // Redirect to Stripe Checkout
    const stripe = await getStripe();
    const { error } = await stripe.redirectToCheckout({
      sessionId: data.sessionId,
    });
    
    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('❌ Credit pack purchase failed:', error);
    throw new Error(
      error.message || 'Failed to start purchase. Please try again.'
    );
  }
}

/**
 * Add an add-on to existing subscription
 * 
 * @param {string} addonId - Add-on ID (white_label, extra_agent)
 * @param {string} teamId - Team ID
 * @param {number} quantity - Quantity (default 1)
 * @returns {Promise<object>}
 */
export async function addSubscriptionAddon(addonId, teamId, quantity = 1) {
  try {
    const functions = getFunctions();
    const addAddon = httpsCallable(functions, 'addSubscriptionAddon');
    
    const { data } = await addAddon({
      addonId,
      teamId,
      quantity,
    });
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    return data;
  } catch (error) {
    console.error('❌ Add-on purchase failed:', error);
    throw new Error(
      error.message || 'Failed to add subscription add-on. Please try again.'
    );
  }
}

/**
 * Upgrade or downgrade subscription plan
 * 
 * @param {string} newPlanId - New plan ID
 * @param {string} teamId - Team ID
 * @param {string} interval - Billing interval ('month' or 'year')
 * @returns {Promise<object>}
 */
export async function changePlan(newPlanId, teamId, interval = 'month') {
  try {
    const functions = getFunctions();
    const updateSubscription = httpsCallable(functions, 'updateSubscriptionPlan');
    
    const { data } = await updateSubscription({
      newPlanId,
      teamId,
      interval,
    });
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    return data;
  } catch (error) {
    console.error('❌ Plan change failed:', error);
    throw new Error(
      error.message || 'Failed to change plan. Please try again.'
    );
  }
}

/**
 * Cancel subscription (at end of billing period)
 * 
 * @param {string} teamId - Team ID
 * @returns {Promise<object>}
 */
export async function cancelSubscription(teamId) {
  try {
    const functions = getFunctions();
    const cancel = httpsCallable(functions, 'cancelSubscription');
    
    const { data } = await cancel({ teamId });
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    return data;
  } catch (error) {
    console.error('❌ Cancellation failed:', error);
    throw new Error(
      error.message || 'Failed to cancel subscription. Please try again.'
    );
  }
}

/**
 * Resume a canceled subscription (before period ends)
 * 
 * @param {string} teamId - Team ID
 * @returns {Promise<object>}
 */
export async function resumeSubscription(teamId) {
  try {
    const functions = getFunctions();
    const resume = httpsCallable(functions, 'resumeSubscription');
    
    const { data } = await resume({ teamId });
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    return data;
  } catch (error) {
    console.error('❌ Resume failed:', error);
    throw new Error(
      error.message || 'Failed to resume subscription. Please try again.'
    );
  }
}

/**
 * Format price for display
 * @param {number} amount - Amount in dollars
 * @param {string} interval - 'month', 'year', or null for one-time
 * @returns {string}
 */
export function formatPrice(amount, interval = null) {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
  
  if (!interval) return formatted;
  
  const suffix = interval === 'year' ? '/year' : '/month';
  return `${formatted}${suffix}`;
}

/**
 * Calculate monthly equivalent for yearly pricing
 * @param {number} yearlyPrice - Yearly price
 * @returns {number} Monthly equivalent
 */
export function getMonthlyEquivalent(yearlyPrice) {
  return yearlyPrice / 12;
}

/**
 * Calculate savings for yearly vs monthly
 * @param {number} monthlyPrice - Monthly price
 * @param {number} yearlyPrice - Yearly price
 * @returns {object} { amount, percent }
 */
export function calculateYearlySavings(monthlyPrice, yearlyPrice) {
  const yearlyIfMonthly = monthlyPrice * 12;
  const savings = yearlyIfMonthly - yearlyPrice;
  const percent = Math.round((savings / yearlyIfMonthly) * 100);
  
  return {
    amount: savings,
    percent,
    formatted: formatPrice(savings),
    message: `Save ${formatPrice(savings)} (${percent}% off)`
  };
}


